import kommuner from '../constants/kommuner'
import { sortOnKey } from '../utils/misc'
import * as nätc from '../constants/nat'

const nätLabels = {
	"SWEREF": "sweref",
	"RIX 95": "rix95",
	"Riksavvägningen": "riksavvägningen"
}

export function nät(stompunkt) {
	return nätLabels[stompunkt.properties.stomnat.natNamn]
}

export function nätNamn(stompunkt) {
	return stompunkt.properties.stomnat.natNamn
}

export function isPlan(stompunkt) {
	return nätNamn(stompunkt) === nätc.PLAN
}

export function isHöjd(stompunkt) {
	return nätNamn(stompunkt) === nätc.HÖJD
}

export function miniDescription(stompunkt) {
	return `${typAndKlass(stompunkt)}: ${stompunkt.properties.namn ? stompunkt.properties.namn : "(namn saknas)"}`
}

export function typAndKlass(stompunkt) {
	if (isHöjd(stompunkt)) {
		return "Höjd"
	}
	if (isPlan(stompunkt)) {
		return `Plan, klass ${mbPlan(stompunkt).kvalitetKlass ? mbPlan(stompunkt).kvalitetKlass : "(kvalitetsklass saknas)" }`
	}
}

export function isFörstörd(stompunkt) {
	return stompunkt.properties.historik.forstord != null
}

export function coordinates2d(stompunkt) {
	return stompunkt.geometry.coordinates.slice(0, 2)
}

export function prettyId(stompunkt) {
	return prettyIdFromId(stompunkt.id)
}

const ID_PREFIX = '202100-4888_'

function startsWithPrefix(id) {
	return id.startsWith(ID_PREFIX)
}

export function prettyIdFromId(id) {
	return startsWithPrefix(id)
		? id.substring(ID_PREFIX.length)
		: id
}

export function fullIdFromId(id) {
	return startsWithPrefix(id)
		? id
		: ID_PREFIX + id
}

export function alternativaId(stompunkt) {
	const alternativaId = stompunkt.properties.alternativId || []
	return alternativaId.join("\n")
}

export function plats(stompunkt) {
	const kommunkod = stompunkt.properties.kommun
	const länskod = stompunkt.properties.lan
	const kommun = kommuner[kommunkod] || ""
	const län = kommuner[länskod] || ""
	return `${kommun} (${kommunkod})\n${län} (${länskod})`
}

export function markering(stompunkt) {
	const markering = stompunkt.properties.markering
	return markeringBas(markering)
		+ (markering.underlag ? ' i ' + markering.underlag : '')
		+ (markering.tillbehor ? ' med ' + markering.tillbehor.join(', ') : '')
}

function markeringBas(markering) {
	if (markering.material == null) {
		return markering.markeringTyp
	}
	else {
		let bas = `${markering.markeringTyp} av ${markering.material}`
		return combos[bas] || bas
	}
}

const combos = {
	"dubb av stål": "ståldubb",
	"dubb av järn": "järndubb",
	"dubb av koppar": "koppardubb",
	"dubb av mässing": "mässingsdubb",
}

export function historik(stompunkt) {
	const historikData = stompunkt.properties.historik || {}
	const eventNames = [
		{ key: 'markerad', name: 'Markerad' },
		{ key: 'aterfunnen', name: 'Återfunnen' },
		{ key: 'ejAterfunnen', name: 'Ej återfunnen' },
		{ key: 'forstord', name: 'Förstörd' }
	]
	const events = []
	for (const { key, name } of eventNames) {
		if (historikData[key]) {
			events.push({ event: name, year: historikData[key] })
		}
	}
	return sortOnKey(events, 'year')
		.map(({ event, year }) => year + ' ' + event)
		.join('\n')
}

export function planKoordinaterString(stompunkt, srid) {
	if(srid === 4976){ // cart
		const decimals = decimalCountPlanByKvalitet(stompunkt);
		const cart = stompunkt.properties.sweref99Kartesisk;
		return cart ? formatCoordinatesCart(cart, decimals) : 'saknas';
	}
	else{
		const coordinates = coordinates2d(stompunkt);
		const decimals = decimalCountPlan(stompunkt);
		return srid === 4619
			? formatCoordinatesLatLong(coordinates, decimals)
			: formatCoordinatesProjected(coordinates, decimals);
	}
}

function formatCoordinatesCart(cartCoordinates, decimals){
	const string = 
		'X ' + cartCoordinates.x.toFixed(decimals) +
		'\nY ' + cartCoordinates.y.toFixed(decimals) +
		'\nZ ' + cartCoordinates.z.toFixed(decimals);
	return string;
}

function formatCoordinatesProjected(coordinates, decimals) {
	return 'N ' + coordinates[1].toFixed(decimals) + ' E ' + coordinates[0].toFixed(decimals)
}

function formatCoordinatesLatLong(coordinates, decimalsOnMeters) {
	const decimals = decimalsOnMeters + 2
	return ddToDms(coordinates[1], decimals) + " N\n" + ddToDms(coordinates[0], decimals) + " E"
}

function ddToDms(dd, decimalsOnSeconds) {
	const d = Math.floor(dd)
	const m = Math.floor((dd - d) * 60)
	const s = (dd - d - m/60) * 3600
	return `${d}° ${m}' ${s.toFixed(decimalsOnSeconds)}"`
}

function decimalCountPlan(stompunkt) {
	return stompunkt.properties.mbPlan.matmetod === "statisk GNSS" ? 3 : stompunkt.properties.mbPlan.ursprung === "RTK" ? 1 : 0
}

function decimalCountPlanByKvalitet(stompunkt) {
	return stompunkt.properties.mbPlan.kvalitetKlass === "1" ? 4 : 3;
}

export function höjdKoordinatString(stompunkt) {
	const höjd = stompunkt.geometry.coordinates[2]
	return höjd
		? höjd.toFixed(decimalCountHöjd(stompunkt))
		: null
}

function decimalCountHöjd(stompunkt) {
	const kvalitetsklass = mbHojd(stompunkt).kvalitetKlass
	return kvalitetsklass === "0" || kvalitetsklass === "1" ? 3 : 2
}

export function lägesosäkerhetPlan(stompunkt) {
	return addMeterUnit(stompunkt.properties.mbPlan.lagesosakerhet)
}

export function lägesosäkerhetHöjd(stompunkt) {
	return addMeterUnit(mbHojd(stompunkt).lagesosakerhet)
}

export function äldreHöjdsystem(stompunkt){
	const aldreHojdsystem = stompunkt.properties.aldreHojdsystem;
	if(aldreHojdsystem){
		const parts = [
			combineValueAndName(aldreHojdsystem.rhb70, 'RHB 70', 3),
			combineValueAndName(aldreHojdsystem.rh70 , 'RH 70', 3),
			combineValueAndName(aldreHojdsystem.rh00 , 'RH 00', 3)
		];
		return parts.filter(p => p).join('\n');	
	}
	else{
		return '';
	}
}

function combineValueAndName(value, name, decimals){
	if(value){
		value = value.toFixed(decimals);
		return `${value} (${name})`;
	}
	else{
		return undefined;
	}
}

function addMeterUnit(value) {
	return value == null
		? undefined
		: value.toString() + ' m'
}

export function mbHojd(stompunkt) {
	return stompunkt.properties.mbHojd || {};
}

export function mbPlan(stompunkt) {
	return stompunkt.properties.mbPlan || {};
}

export function firstBild(stompunkt) {
	return stompunkt.properties.bild == null
		? undefined
		: stompunkt.properties.bild[0]
}

export function anmärkning(stompunkt, anmärkningstyp) {
	const allAnmärkningar = stompunkt.properties.anmarkning || []
	const correctAnmärkning = allAnmärkningar.find(
		(anmärkning) => anmärkning.anmarkningTyp === anmärkningstyp
	) 
	return correctAnmärkning ? replaceBreaks(correctAnmärkning.anmarkningText) : undefined
}

function replaceBreaks(value) {
	return value == null
		? undefined
		: value.replace(/<br>/g, "\n").trim()
}

export function mapColorCategory(stompunkt) {
	return isFörstörd(stompunkt)
		? "förstörd"
		: layer(stompunkt)
}

export function layer(stompunkt) {
	if (isHöjd(stompunkt)) {
		return "riksavvägningen"
	}
	if (isPlan(stompunkt)) {
		return mbPlan(stompunkt).kvalitetKlass === "1" ? "sweref" : "rix95"
	}
}

export function passFilter(stompunkt, filter) {
	return passFörstördaFilter(filter, stompunkt) 
		&& passNätAndKlassFilter(filter, stompunkt)
}

const passFörstördaFilter = (filter, stompunkt) => filter.förstörda || !isFörstörd(stompunkt)
const passNätAndKlassFilter = (filter, stompunkt) => filter[layer(stompunkt)]



// WEBPACK FOOTER //
// ./src/selectors/stompunkt.js